import React, { useState } from 'react';
import { Bell, Mail, MessageSquare, Gift, AlertTriangle } from 'lucide-react';

const NotificationSettings = () => {
    const [settings, setSettings] = useState({
        orderUpdates: {
            email: false,
            sms: true,
            push: true
        },
        subscriptionRenewal: {
            email: true,
            push: false
        },
        promotions: {
            email: false,
            push: false
        },
        systemAlerts: {
            email: true,
            push: true
        }
    });

    const handleToggle = (category, channel) => {
        setSettings(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [channel]: !prev[category][channel]
            }
        }));
    };

    const handleSave = () => {
        // Mock API call
        console.log('Saving notification settings:', settings);
        alert('Notification settings saved successfully!');
    };

    const ToggleSwitch = ({ checked, onChange, disabled = false }) => (
        <label className="relative inline-flex items-center cursor-pointer">
            <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                disabled={disabled}
                className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
    );

    const NotificationSection = ({ title, description, icon: Icon, iconColor, category, channels }) => (
        <div className="bg-white rounded-lg md:rounded-xl border border-gray-200 p-4 md:p-5 hover:shadow-md transition-all">
            <div className="flex items-start gap-3 mb-3">
                <div className={`w-10 h-10 md:w-12 md:h-12 ${iconColor} bg-opacity-10 rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <Icon size={20} className={iconColor} />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm md:text-base font-bold text-gray-900 mb-1 truncate">{title}</h3>
                    <p className="text-xs text-gray-600 line-clamp-2">{description}</p>
                </div>
            </div>

            <div className="space-y-2 md:space-y-2.5">
                {channels.map(channel => (
                    <div key={channel.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <channel.icon size={16} className="text-gray-400" />
                            <span className="text-xs md:text-sm font-medium text-gray-700">{channel.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold ${settings[category][channel.id] ? 'text-blue-600' : 'text-gray-400'}`}>
                                {settings[category][channel.id] ? 'ON' : 'OFF'}
                            </span>
                            <ToggleSwitch
                                checked={settings[category][channel.id]}
                                onChange={() => handleToggle(category, channel.id)}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="h-full flex flex-col p-4 md:p-6 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex-shrink-0 mb-4">
                <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Notification Settings
                </h2>
                <p className="text-xs md:text-sm text-gray-500 mt-1">Manage how you receive notifications</p>
            </div>

            {/* Notification Sections - Scrollable if needed */}
            <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 mb-4">
                    <NotificationSection
                        title="Order Updates"
                        description="Receive notifications on new orders, order status updates, and completed orders."
                        icon={Bell}
                        iconColor="text-blue-600"
                        category="orderUpdates"
                        channels={[
                            { id: 'email', label: 'Email', icon: Mail },
                            { id: 'sms', label: 'SMS', icon: MessageSquare },
                            { id: 'push', label: 'Push', icon: Bell }
                        ]}
                    />

                    <NotificationSection
                        title="Subscription Renewal"
                        description="Be notified of subscription renewal charges and renewal reminders."
                        icon={Bell}
                        iconColor="text-purple-600"
                        category="subscriptionRenewal"
                        channels={[
                            { id: 'email', label: 'Email', icon: Mail },
                            { id: 'push', label: 'Push', icon: Bell }
                        ]}
                    />

                    <NotificationSection
                        title="Promotions and Offers"
                        description="Receive notifications about new promotions, discounts, and special offers."
                        icon={Gift}
                        iconColor="text-orange-600"
                        category="promotions"
                        channels={[
                            { id: 'email', label: 'Email', icon: Mail },
                            { id: 'push', label: 'Push', icon: Bell }
                        ]}
                    />

                    <NotificationSection
                        title="System Alerts"
                        description="Get notified of important account alerts and security updates."
                        icon={AlertTriangle}
                        iconColor="text-red-600"
                        category="systemAlerts"
                        channels={[
                            { id: 'email', label: 'Email', icon: Mail },
                            { id: 'push', label: 'Push', icon: Bell }
                        ]}
                    />
                </div>
            </div>

            {/* Save Button */}
            <div className="flex-shrink-0 flex justify-end pt-4 border-t border-gray-200">
                <button
                    onClick={handleSave}
                    className="px-6 md:px-8 py-2.5 md:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold shadow-md hover:shadow-lg text-sm md:text-base"
                >
                    Save Changes
                </button>
            </div>
        </div>
    );
};

export default NotificationSettings;
